/**
 * AnnotatorCategory — the annotator role's per-category page
 * (`/labeling-overview/:category` when the resolved role is "annotator").
 * Markup mirrors mockup `#s-acat`
 * (PLANS/reviews/2026-07-07-labeling-overview-redesign-mockup.html):
 * a resume hero for the first active enrollment, one `ProjectWorklistGroup`
 * per enrolled project ("Your projects"), and an accept-card list of
 * projects not yet joined ("Available to you").
 *
 * The "Study view" control is the shared `WorklistDropdown` (Task 6),
 * populated from the resume enrollment's sibling worklists.
 *
 * `AvailableProject` (src/types/factory.ts) has no `project_type` field, so
 * "Available to you" can't be filtered to this category — the same data-gap
 * `CategoryHome`'s `useActiveWorkKeys` documents for admin/internal roles.
 * Guessing a category here would be a silently wrong signal, so the accept
 * pool is shown unfiltered until the backend adds one.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useAcceptProjectMutation,
  useAvailableProjectsQuery,
  useMyEnrollmentsQuery,
} from "../../../hooks/useFactoryQueries";
import type { EnrollmentState, MyEnrollment } from "../../../types/factory";
import { batchSeq } from "../../../utils/factoryDisplay";
import { CATEGORIES, type LabelingCategoryKey } from "../../../utils/labelingCategories";
import { logger } from "../../../utils/logger";
import { categoryAccent } from "./categoryAccent";
import { CountStrip, LoadingPanel, SectionLabel, SquareList, SquareRow } from "./primitives";
import { ProjectWorklistGroup } from "./ProjectWorklistGroup";
import { WorklistDropdown, type WorklistDropdownItem } from "./WorklistDropdown";

const STATE_LABEL: Record<EnrollmentState, string> = {
  active: "active",
  pending_approval: "submitted for review",
  blocked_pool_empty: "blocked (pool empty)",
  completed: "completed",
};

interface AnnotatorCategoryProps {
  categoryKey: LabelingCategoryKey;
}

export function AnnotatorCategory({ categoryKey }: AnnotatorCategoryProps) {
  const navigate = useNavigate();
  const meta = CATEGORIES.find((c) => c.key === categoryKey)!;
  const accent = categoryAccent(categoryKey);

  const myEnrollmentsQuery = useMyEnrollmentsQuery();
  const availableProjectsQuery = useAvailableProjectsQuery();
  const acceptMutation = useAcceptProjectMutation();

  const [poolExhaustedFor, setPoolExhaustedFor] = useState<string | null>(null);

  // No category filter here: this component is mounted only for the factory
  // (3d-segmentation) category (CategoryView `FACTORY_CATEGORIES`), and
  // `/my-enrollments` is segmentation-only, so every returned enrollment belongs
  // to this page. Filtering by `categoryForProjectType(project_type)` used to drop
  // them all, because the backend does not send `project_type` (B1).
  const categoryEnrollments = myEnrollmentsQuery.data ?? [];

  const projectGroups = useMemo(() => {
    const byProject = new Map<string, MyEnrollment[]>();
    for (const e of categoryEnrollments) {
      const list = byProject.get(e.project_id) ?? [];
      list.push(e);
      byProject.set(e.project_id, list);
    }
    return Array.from(byProject.entries()).map(([projectId, worklists]) => ({
      projectId,
      worklists,
    }));
  }, [categoryEnrollments]);

  const availableProjects = availableProjectsQuery.data ?? [];

  const resumeEnrollment = categoryEnrollments.find(
    (e) => !e.annotation_read_only && e.state === "active" && !!e.worklist_project_id,
  );
  const resumeGroup = resumeEnrollment
    ? projectGroups.find((g) => g.projectId === resumeEnrollment.project_id)
    : undefined;

  const inProgressCount = categoryEnrollments.filter(
    (e) => !e.annotation_read_only && e.state === "active",
  ).length;
  const awaitingReviewCount = categoryEnrollments.filter(
    (e) => !e.annotation_read_only && e.state === "pending_approval",
  ).length;

  const onOpenStudy = (worklistProjectId: string) => {
    navigate(`/labeling-overview/${categoryKey}/${encodeURIComponent(worklistProjectId)}`);
  };

  const studyViewItems: WorklistDropdownItem[] = resumeGroup
    ? resumeGroup.worklists
        .filter((w) => !!w.worklist_project_id)
        .map((w) => ({
          id: w.worklist_project_id as string,
          label: w.current_batch_id ? `Batch ${batchSeq(w.current_batch_id)}` : "No batch",
          tag: STATE_LABEL[w.state],
          tone: w.state === "active" ? ("sig" as const) : ("ghost" as const),
        }))
    : [];

  const handleContinue = () => {
    if (!resumeEnrollment?.worklist_project_id) return;
    navigate(`/labeling?project=${encodeURIComponent(resumeEnrollment.worklist_project_id)}`);
  };

  const handleAccept = (projectId: string) => {
    setPoolExhaustedFor(null);
    acceptMutation.mutate(projectId, {
      onSuccess: (batch: { worklist_project_id?: string } | undefined) => {
        if (!batch?.worklist_project_id) return;
        navigate(`/labeling?project=${encodeURIComponent(batch.worklist_project_id)}`);
      },
      onError: (err: unknown) => {
        if ((err as { poolExhausted?: true })?.poolExhausted) {
          setPoolExhaustedFor(projectId);
          return;
        }
        logger.error("[AnnotatorCategory] accept failed", err);
      },
    });
  };

  return (
    <div className="h-full overflow-auto text-white page-premium">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate("/labeling-overview")}
          className="mb-4 text-xs text-fg-muted transition hover:text-fg"
        >
          ← Labeling
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className={`w-0.5 self-stretch rounded-sharp ${accent.edge}`}
            />
            <h1 className="text-2xl font-bold text-fg">{meta.name}</h1>
          </div>
          <div className="mt-3">
            <CountStrip
              items={[
                `${projectGroups.length} enrolled`,
                ...(inProgressCount > 0 ? [`${inProgressCount} in progress`] : []),
                ...(awaitingReviewCount > 0 ? [`${awaitingReviewCount} awaiting review`] : []),
                `${availableProjects.length} available`,
              ]}
            />
          </div>
        </div>

        {resumeEnrollment && resumeGroup && (
          <div
            data-testid="resume-hero"
            className="mb-6 flex items-center gap-4 rounded border border-border/50 bg-surface p-5"
          >
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-dim">
                Resume
              </div>
              <div className="mt-1 truncate text-[17px] font-semibold text-fg">
                {resumeEnrollment.project_name ?? resumeEnrollment.project_id}
              </div>
              <div className="mt-1 text-[12.5px] text-fg-muted">
                Phase {resumeEnrollment.phase}
                {resumeEnrollment.current_batch_id
                  ? ` · Batch ${batchSeq(resumeEnrollment.current_batch_id)}`
                  : ""}
              </div>
            </div>
            <span className="flex-1" />
            <div className="flex shrink-0 items-center gap-2.5">
              <WorklistDropdown
                label="Study view"
                worklists={studyViewItems}
                // Deliberately no selectedId: this is an action menu to jump to a
                // worklist's study view (mockup #s-acat cur text is the static
                // "Open a worklist", not the resumed batch name), unlike
                // StudyView's own dropdown which reflects the page's active
                // worklist. Passing the resumed enrollment's id here would also
                // duplicate the batch label already shown in the "Your
                // projects" row below.
                selectedId={null}
                onSelect={onOpenStudy}
              />
              <button
                type="button"
                onClick={handleContinue}
                className="rounded bg-primary px-4 py-2 text-xs font-semibold text-white transition hover:bg-primary/90"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        <SectionLabel eyebrow="Your projects" note="a batch row opens its study view" />
        {myEnrollmentsQuery.isLoading ? (
          <LoadingPanel label="Loading your projects…" />
        ) : myEnrollmentsQuery.error ? (
          <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
            Failed to load your projects
          </div>
        ) : projectGroups.length === 0 ? (
          <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
            No projects yet
          </div>
        ) : (
          <div className="flex flex-col gap-3.5">
            {projectGroups.map((group) => (
              <ProjectWorklistGroup
                key={group.projectId}
                project={group.worklists[0]}
                worklists={group.worklists}
                onOpenStudy={onOpenStudy}
              />
            ))}
          </div>
        )}

        <SectionLabel eyebrow="Available to you" note="accept to start the demo phase" />
        {availableProjectsQuery.isLoading ? (
          <LoadingPanel label="Loading available projects…" />
        ) : availableProjectsQuery.error ? (
          <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
            Failed to load available projects
          </div>
        ) : availableProjects.length === 0 ? (
          <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
            Nothing available right now
          </div>
        ) : (
          <SquareList>
            {availableProjects.map((project) => (
              <SquareRow key={project.project_id}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-fg">{project.name}</div>
                  <div className="truncate text-xs text-fg-muted">
                    {project.project_id} · {project.task}
                  </div>
                </div>
                {poolExhaustedFor === project.project_id && (
                  <span className="text-xs text-warn">
                    Pool empty — nothing to accept right now
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleAccept(project.project_id)}
                  disabled={acceptMutation.isPending}
                  className="shrink-0 rounded bg-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Accept
                </button>
              </SquareRow>
            ))}
          </SquareList>
        )}
      </div>
    </div>
  );
}

export default AnnotatorCategory;
